import React from "react";
import { Input } from "@trussle/external-components";
import { useCalculatorStore } from "../store";

import "./styles.scss";
import { calculateMonthlyRepaymentAmount, convertCurrencyStringToFloat } from "../../CalculatorPanels/calculatorMethods";
import { formatNumberWithCommas } from "../../CalculatorPanels/stringFormatMethods";
import { CalculatorTabLayout } from "../CalculatorTabLayout";
import { Hr } from "../_Atoms/Hr";
import { redirect } from "../../../../helpers/redirect";
import { trackEvent } from "../../../../helpers/analytics";

interface IRemortgageCalculatorProps {
  mortgageRateBasedOnLtv?: { defaultValue: number, rates: { ltv: number, mortgageRate: number }[]};
}

const Disclaimer = (
  <div className="calculator-panel-v2__disclaimer">
    Your home could be repossessed if you don't keep up repayments on your mortgage.
    <br />
    Talk to a
    {" "}
    <a
      href="/"
      aria-label="Mortgage broker"
      target="_blank"
      rel="noreferrer"
    >
      mortgage broker
    </a>
    {" "}
    or lender to get a more accurate remortgage savings amount.
    You may have to pay an early repayment charge if you remortgage.
  </div>
);

const handleCTA = async () => {
  await redirect("https://better.co.uk/get-started/remaining-term");

  await trackEvent("Remortgage calculator cta clicked", {
    module: "Remortgage calculator",
  });
};

export const RemortgageCalculator: React.FunctionComponent<IRemortgageCalculatorProps> = ({ mortgageRateBasedOnLtv }) => {
  const {
    propertyValue,
    outstandingMortgageAmount,
    currentlyMonthlyPayment,
    mortgageTerm,
    setPropertyValue,
    setOutstandingMortgageAmount,
    setCurrentlyMonthlyPayment,
    setMortgageTerm,
  } = useCalculatorStore();

  // F for float
  const propertyValueF = convertCurrencyStringToFloat(propertyValue);
  const outstandingBorrowAmountF = convertCurrencyStringToFloat(outstandingMortgageAmount);
  const LTV = outstandingBorrowAmountF > 0
    ? outstandingBorrowAmountF / propertyValueF
    : 0;

  const getMortgageRate = () => {
    if (mortgageRateBasedOnLtv) {
      const index = Math.floor(LTV * 10) / 10;
      let maxPossibleLTV = 0;

      const mortgageRateBasedOnLtvMap: {[key: string]: number} = mortgageRateBasedOnLtv.rates.reduce((total, { ltv, mortgageRate }) => {
        maxPossibleLTV = Math.max(maxPossibleLTV, ltv);
        return {
          ...total,
          [ltv]: mortgageRate,
        };
      }, {});

      if (LTV >= maxPossibleLTV) {
        return mortgageRateBasedOnLtvMap[maxPossibleLTV];
      }

      return mortgageRateBasedOnLtvMap[index] ?? mortgageRateBasedOnLtv.defaultValue;
    }

    return 6.34;
  };

  const borrowAmount = parseFloat(
    (outstandingMortgageAmount && outstandingMortgageAmount.replace(/,/g, ""))
    || "0",
  );
  const monthlyInterestRate = getMortgageRate() / 100 / 12;
  const numberOfMonths = parseInt(mortgageTerm || "0", 10) * 12;
  const currentMonthlyRepaymentF = parseFloat(
    (currentlyMonthlyPayment && currentlyMonthlyPayment.replace(/,/g, ""))
    || "0",
  );
  const newMonthlyRepayment = Math.round(
    calculateMonthlyRepaymentAmount(
      borrowAmount,
      monthlyInterestRate,
      numberOfMonths,
    ),
  );
  const monthlySaveAmount = currentMonthlyRepaymentF - newMonthlyRepayment;
  const onYearSaveAmountF = monthlySaveAmount * 12;
  const onYearSaveAmountDisplay = (
    !Number.isNaN(monthlySaveAmount)
    && monthlySaveAmount !== undefined
    && monthlySaveAmount !== Infinity
    && monthlySaveAmount !== -Infinity)
    ? `£${formatNumberWithCommas(Math.round(onYearSaveAmountF).toString())}`
    : "";

  const isSavingsPositive = onYearSaveAmountF !== undefined && onYearSaveAmountF > 0;
  const isDataFilled = convertCurrencyStringToFloat(propertyValue)
    && convertCurrencyStringToFloat(outstandingMortgageAmount)
    && convertCurrencyStringToFloat(currentlyMonthlyPayment)
    && convertCurrencyStringToFloat(mortgageTerm);

  const form = (
    <>
      <Input
        betterV2
        variant="currency"
        name="Property value"
        label="Property value"
        inputSize="large"
        id="remortgage-calculator-property-value"
        data-testid="remortgage-calculator-property-value"
        classOverrides={{ inputContainer: "calculator-input" }}
        value={propertyValue}
        required
        onChange={(e) => {
          setPropertyValue(e.target.value);
        }}
      />
      <Input
        betterV2
        variant="currency"
        name="Outstanding mortgage amount"
        label="Outstanding mortgage amount"
        inputSize="large"
        id="remortgage-calculator-outstanding-mortgage-amount"
        data-testid="remortgage-calculator-outstanding-mortgage-amount"
        classOverrides={{ inputContainer: "calculator-input" }}
        value={outstandingMortgageAmount}
        onChange={(e) => {
          setOutstandingMortgageAmount(e.target.value);
        }}
      />
      <Input
        betterV2
        variant="currency"
        name="Current monthly repayment"
        label="Current monthly repayment"
        inputSize="large"
        id="remortgage-calculator-currently-monthly-repayment"
        data-testid="remortgage-calculator-currently-monthly-repayment"
        classOverrides={{ inputContainer: "calculator-input" }}
        value={currentlyMonthlyPayment}
        required
        onChange={(e) => {
          setCurrentlyMonthlyPayment(e.target.value);
        }}
      />
      <Input
        betterV2
        variant="year"
        name="Mortgage term"
        label="Mortgage term (years)"
        inputSize="large"
        id="remortgage-calculator-mortgage-term"
        data-testid="remortgage-calculator-mortgage-term"
        classOverrides={{ inputContainer: "calculator-input" }}
        value={mortgageTerm}
        required
        onChange={(e) => {
          setMortgageTerm(e.target.value);
        }}
      />
    </>
  )

  const result = (
    <div className="remortgage-calculator">
      <div className="calculator-panel-v2__horizontal-line calculator-panel-v2__result__header">
        {isSavingsPositive || !onYearSaveAmountF || !isDataFilled ? (
          <>
            <h3>You could save up to</h3>
            <span>
              {onYearSaveAmountDisplay}
            </span>
          </>
        ) : (
          <span className="remortgage-calculator__negative-outcome">
            It looks like you have a good mortgage deal and don’t need to remortgage
          </span>
        )}
      </div>
      {isSavingsPositive && <h6 className="remortgage-calculator__savings-info">per year on your mortgage</h6>}
      <Hr />
      <div className="calculator-panel-v2__vertical-line ">
        <p className="remortgage-calculator__result-info">
          This is based on a new 2-year fixed or tracker rate mortgage with an interest rate of
          {" "}
          {getMortgageRate()}
          %.
        </p>
        <p className="remortgage-calculator__result-info">Your savings will vary depending on your circumstances. See below for how we calculate your potential savings.</p>
      </div>
    </div>
  )

  return (
    <CalculatorTabLayout
      variant="Remortgage"
      form={form}
      result={result}
      ctaHandler={handleCTA}
      disclaimer={Disclaimer}
    />
  );
};
